import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useFormTemplate, useFormVersion } from "../../../lib/queries/formTemplates";
import { useDeleteProjectRequirement } from "../../../lib/queries/projectCompleteness";

/** Retires the preparation only. Invitations and signed evidence are never cancelled. */
export function PreparedMandateChoice({
  projectId,
  requirementId,
  versionId,
  used,
  onRetired,
}: {
  projectId: string;
  requirementId: string;
  versionId: string;
  used: boolean;
  onRetired: () => void;
}) {
  const { t, i18n } = useTranslation();
  const version = useFormVersion(versionId.toLowerCase());
  const template = useFormTemplate(version.data?.template_id);
  const unavailable = version.isError || template.isError;
  const identity =
    !unavailable && version.data && template.data
      ? `${template.data.template.name} · ${t("request_flow.version", { number: version.data.version_number })}`
      : null;
  const identityLabel =
    identity ?? t(unavailable ? "request_flow.prepared_mandate_unavailable" : "app.loading");
  const retire = useDeleteProjectRequirement(projectId);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const wasConfirming = useRef(false);
  useEffect(() => {
    if (confirm) cancel.current?.focus();
    else if (wasConfirming.current) trigger.current?.focus();
    wasConfirming.current = confirm;
  }, [confirm]);
  function close() {
    setConfirm(false);
  }
  async function submit() {
    if (retire.isPending || used || !identity) return;
    setError(null);
    try {
      await retire.mutateAsync(requirementId);
      onRetired();
    } catch (err) {
      setError(
        err instanceof ApiException
          ? getApiErrorMessage(err, normalizeLang(i18n.language))
          : t("errors.generic"),
      );
    }
  }

  return (
    <div className="space-y-2 rounded-lg border border-slate-200 p-3">
      <p
        className="break-words font-medium"
        role={identity ? undefined : unavailable ? "alert" : "status"}
      >
        {identityLabel}
      </p>
      <p className="text-sm text-slate-600">
        {t(used ? "request_flow.prepared_mandate_used" : "request_flow.prepared_mandate_help")}
      </p>
      {used ? null : !confirm ? (
        <button
          disabled={!identity}
          ref={trigger}
          type="button"
          className="btn min-h-11"
          onClick={() => setConfirm(true)}
        >
          {t("request_flow.change_mandate_model")}
        </button>
      ) : (
        <div
          role="group"
          aria-label={t("request_flow.change_mandate_model")}
          onKeyDown={(e) => {
            if (e.key === "Escape" && !retire.isPending) {
              e.preventDefault();
              close();
            }
          }}
        >
          <p>{t("request_flow.prepared_mandate_confirm", { identity: identityLabel })}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              ref={cancel}
              type="button"
              className="btn min-h-11"
              disabled={retire.isPending}
              onClick={close}
            >
              {t("request_flow.keep_mandate_model")}
            </button>
            <button
              type="button"
              className="btn min-h-11"
              disabled={retire.isPending || !identity}
              onClick={() => void submit()}
            >
              {t("request_flow.retire_mandate_preparation")}
            </button>
          </div>
        </div>
      )}
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}
